import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ChevronDownIcon, Clock3Icon, RefreshCwIcon, TrashIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { Button } from 'ui/button'
import { Input } from 'ui/input'
import { Textarea } from 'ui/text-area'

import { SnoozeCountBadge } from '~/boundedContext/cards/components/snooze-count-badge'
import { getActiveSnoozeCount, isCardSnoozedForFront } from '~/boundedContext/cards/utils/card'
import { getSnoozeButtonClassName, getSnoozeEditCardClassName } from '~/boundedContext/cards/utils/snooze-palette'
import type { Card } from '~/db/types'
import { useSettingsStore } from '~/stores/settings'

import { useExpandedStore } from '../../stores/expanded'
import { SnoozeCardDrawer } from '../snooze-card-drawer'

import { COLLAPSED_CARD_HEIGHT, EXPANDED_CARD_HEIGHT } from './const'
import { useSyncUncontrolledInput } from './hooks'
import { EditCardProps } from './types'

export const EditCard = <T extends Card>({
  card,
  autoFocusLearnText,
  autoFocusSourceText,
  onRefresh,
  onLearnTextChange,
  onSourceTextChange,
  onPronunciationChange,
  onRemove,
  onSnoozeForCount,
  onUnsnooze,
  onExamplesChange,
  ref,
  onExpandAnimationComplete,
  onExpandHeightAnimationIteration,
  onExpand,
  onCollapse,
}: EditCardProps<T>) => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const expanded = useExpandedStore(state => state.expandedSet.has(card.id))
  const toggleExpanded = useExpandedStore(state => state.toggleExpanded)
  const [snoozeDrawerOpen, setSnoozeDrawerOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const mainContentRef = useRef<HTMLDivElement>(null)
  const toggleId = useId()
  const detailsId = useId()
  const { t } = useTranslation()
  const sourceTextFieldRef = useSyncUncontrolledInput(card.sourceText)
  const learnTextFieldRef = useSyncUncontrolledInput(card.learnText)
  const pronunciationFieldRef = useSyncUncontrolledInput(card.pronunciation)
  const examplesFieldRef = useSyncUncontrolledInput<HTMLTextAreaElement>(card.examples)
  const cardsFront = useSettingsStore(state => state.cardsFront)
  const isSnoozedForFront = isCardSnoozedForFront(card, cardsFront)
  const activeSnoozeCount = getActiveSnoozeCount(card, cardsFront)

  const expandSpring = {
    type: 'spring' as const,
    stiffness: 420,
    damping: 14,
    bounce: 0.38,
  }

  const collapseTransition = {
    type: 'tween' as const,
    duration: 0.22,
    ease: [0.4, 0, 0.2, 1] as const,
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await onRefresh(card)
    } finally {
      setIsRefreshing(false)
    }
  }

  const onExpandIconClick = () => {
    toggleExpanded(card.id)
    onExpand?.(card)
  }
  const onCollapseIconClick = () => {
    toggleExpanded(card.id)
    onCollapse?.(card)
  }

  const onAnimationUpdate = (values: { height: number }) =>
    onExpandHeightAnimationIteration(card, values.height as number)

  const previousHeight = useRef(expanded ? EXPANDED_CARD_HEIGHT : COLLAPSED_CARD_HEIGHT)
  const onAnimationComplete = ({ height }: { height: number }) => {
    if (previousHeight.current === height) return
    previousHeight.current = height
    // onAnimationComplete run when no animation was running
    onExpandAnimationComplete?.()
  }

  return (
    <div
      ref={node => {
        wrapperRef.current = node
        if (ref) ref.current = node
      }}
      className={clsx(
        'relative h-fit ring-1',
        'overflow-hidden rounded-xl shadow-md backdrop-blur-xl',
        getSnoozeEditCardClassName(card, cardsFront) ||
          'bg-card dark:bg-muted/45 ring-black/[0.06] dark:ring-white/[0.08]',
        'dark:border-white/[0.13] dark:shadow-[0_8px_28px_-10px_rgba(0,0,0,0.48),inset_0_1px_0_0_rgba(255,255,255,0.05)]',
        'max-md:first:mt-auto md:grow md:basis-2/5',
      )}
      id={card.id}
    >
      <motion.div
        animate={{ height: expanded ? EXPANDED_CARD_HEIGHT : COLLAPSED_CARD_HEIGHT }}
        className="flex flex-col overflow-hidden"
        initial={false}
        onAnimationComplete={onAnimationComplete}
        onUpdate={onAnimationUpdate}
        transition={expanded ? expandSpring : collapseTransition}
      >
        <div ref={mainContentRef} className="flex gap-3 p-3">
          <div className="min-w-0 flex-1 space-y-2">
            <Input
              ref={sourceTextFieldRef}
              aria-label={t('source text input')}
              autoFocus={autoFocusSourceText}
              defaultValue={card.sourceText}
              name={`source-text-${card.id}`}
              onChange={e => onSourceTextChange(card, e.currentTarget.value)}
              placeholder={t('Source text')}
            />
            <Input
              ref={learnTextFieldRef}
              aria-label={t('learn text input')}
              autoFocus={autoFocusLearnText}
              defaultValue={card.learnText}
              name={`learn-text-${card.id}`}
              onChange={e => onLearnTextChange(card, e.currentTarget.value)}
              placeholder={t('Learn text')}
            />
          </div>
          <div className="flex shrink-0 flex-col items-center justify-between gap-1 pt-0.5">
            <Button
              aria-label={t('remove item button')}
              className="size-9 text-orange-800 hover:text-orange-600"
              onClick={() => onRemove(card)}
              size="icon"
              variant="ghost"
            >
              <TrashIcon />
            </Button>
            <Button
              aria-controls={detailsId}
              aria-expanded={expanded}
              aria-label={t('accordion toggle button')}
              className="size-9 text-muted-foreground transition-colors hover:bg-accent/80 hover:text-foreground"
              id={toggleId}
              onClick={expanded ? onCollapseIconClick : onExpandIconClick}
              size="icon"
              type="button"
              variant="ghost"
            >
              <ChevronDownIcon className={clsx('transition-transform duration-200', expanded && 'rotate-180')} />
            </Button>
          </div>
        </div>

        <motion.div
          animate={{ opacity: expanded ? 1 : 0 }}
          aria-labelledby={toggleId}
          className={clsx(
            'flex flex-col gap-2 px-3 pb-3 pt-3 dark:border-white/[0.08]',
            !expanded && 'pointer-events-none',
          )}
          id={detailsId}
          inert={!expanded}
          initial={false}
          role="region"
          transition={collapseTransition}
        >
          <div className="flex gap-2">
            <Input
              ref={pronunciationFieldRef}
              aria-label={t('pronunciation text input')}
              className="grow"
              defaultValue={card.pronunciation}
              name={`pronunciation-${card.id}`}
              onChange={e => onPronunciationChange(card, e.currentTarget.value)}
              placeholder={t('Pronunciation')}
              tabIndex={expanded ? undefined : -1}
            />
            <Button
              aria-label={t('refresh example button')}
              className="size-9 shrink-0 text-muted-foreground hover:text-foreground"
              disabled={isRefreshing}
              onClick={handleRefresh}
              size="icon"
              tabIndex={expanded ? undefined : -1}
              type="button"
              variant="ghost"
            >
              <RefreshCwIcon className={clsx(isRefreshing && 'animate-spin')} />
            </Button>
          </div>
          <div className="flex gap-2">
            <Textarea
              ref={examplesFieldRef}
              aria-hidden={!expanded}
              aria-label={t('Example text area input')}
              className="min-w-0 grow"
              defaultValue={card.examples}
              name={`examples-${card.id}`}
              onChange={e => onExamplesChange(card, e.currentTarget.value)}
              placeholder={t('Example')}
              rows={5}
              tabIndex={expanded ? undefined : -1}
            />
            <div className="flex w-10 shrink-0 flex-col items-center gap-1 self-start">
              <Button
                aria-label={isSnoozedForFront ? t('Un-snooze card') : t('Snooze card')}
                className={clsx(
                  'size-9 shrink-0 transition-colors',
                  getSnoozeButtonClassName(card, cardsFront) || 'text-muted-foreground hover:text-foreground',
                )}
                onClick={() => (isSnoozedForFront ? onUnsnooze(card) : setSnoozeDrawerOpen(true))}
                size="icon"
                variant="ghost"
              >
                <Clock3Icon />
              </Button>
              {!!activeSnoozeCount && (
                <SnoozeCountBadge card={card} className="block w-full truncate px-1 text-center" compact />
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
      <SnoozeCardDrawer
        onConfirm={snoozeCount => onSnoozeForCount(card, snoozeCount)}
        onOpenChange={setSnoozeDrawerOpen}
        open={snoozeDrawerOpen}
      />
    </div>
  )
}
